import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { upgradeImageUrl, FALLBACK_HERO, FALLBACK_THUMB } from "@/lib/imageUtils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    TrendingUp,
    ChevronRight,
    Calendar,
    Zap,
    Menu,
    X,
    LogIn,
    LogOut,
    LayoutDashboard,
    Clock,
    Newspaper,
    List,
    RefreshCcw
} from "lucide-react";
import LiveScoresTicker from "@/components/LiveScoresTicker";
import WebSubscribeWidget from "@/components/WebSubscribeWidget";
import DailyPicksCard from "@/components/DailyPicksCard";

// Sports data
const SPORTS = [
    { id: "football", name: "Kandanda", nameEn: "Football", icon: "⚽" },
    { id: "basketball", name: "Mpira wa Kikapu", nameEn: "Basketball", icon: "🏀" },
    { id: "boxing", name: "Ngumi", nameEn: "Boxing", icon: "🥊" },
];

const COMPETITIONS = {
    football: {
        tanzania: [{ id: "nbc_premier", name: "NBC Premier", name_sw: "Ligi Kuu TZ" }],
        england: [{ id: "epl", name: "Premier League", name_sw: "Ligi Kuu UK (EPL)" }],
        europe: [{ id: "ucl", name: "Champions League", name_sw: "Champions League" }],
        spain: [{ id: "laliga", name: "La Liga", name_sw: "La Liga" }],
        italy: [{ id: "serie_a", name: "Serie A", name_sw: "Serie A" }],
        africa: [{ id: "caf_cl", name: "CAF CL", name_sw: "CAF" }],
        world: [{ id: "world_cup", name: "FIFA World Cup", name_sw: "Kombe la Dunia 2026" }],
    }
};

const COUNTRIES = {
    england: { flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" },
    spain: { flag: "🇪🇸" },
    italy: { flag: "🇮🇹" },
    europe: { flag: "🇪🇺" },
    africa: { flag: "🌍" },
    tanzania: { flag: "🇹🇿" },
    world: { flag: "🌐" },
};

const NAV_ITEMS = [
    { id: "scores", name: "Matokeo", nameEn: "Scores", link: "/matokeo" },
    { id: "schedule", name: "Ratiba", nameEn: "Schedule", link: "/mechi" },
    { id: "standings", name: "Msimamo", nameEn: "Standings", link: "/msimamo" },
    { id: "predictions", name: "Utabiri", nameEn: "Predictions", link: "/utabiri" },
    { id: "my-slip", name: "Utabiri Wangu", nameEn: "My Slip", link: "/utabiri-wangu" },
];

// Swahili labels for content_type enum values
const CONTENT_TYPE_SW = {
    news: "Habari",
    team_news: "Habari za Timu",
    pre_match: "Kabla ya Mechi",
    match_report: "Taarifa ya Mechi",
    post_match: "Baada ya Mechi",
    analysis: "Uchambuzi",
    transfer: "Uhamisho",
    injury_update: "Majeruhi",
    standings: "Msimamo",
    fixtures: "Ratiba",
    highlights: "Muhtasari",
    interview: "Mahojiano",
    opinion: "Maoni",
};

const HomePage = () => {
    const { user, logout, isContentManager } = useAuth();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [heroSlides, setHeroSlides] = useState([]);
    const [heroIdx, setHeroIdx] = useState(0);
    const [topHeadlines, setTopHeadlines] = useState([]);
    const [predictions, setPredictions] = useState([]);
    const [matches, setMatches] = useState([]);
    const [tanzaniaNews, setTanzaniaNews] = useState([]);
    const [selectedSport, setSelectedSport] = useState("football");
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        fetchContent();
        fetchPredictions();
        fetchMatches();
        fetchTanzaniaNews();
    }, [selectedSport, selectedCompetition]);

    // Auto-poll content + matches + predictions every 90s so the homepage
    // stays current without forcing a manual reload.
    useEffect(() => {
        const t = setInterval(() => {
            fetchContent();
            fetchMatches();
            fetchTanzaniaNews();
        }, 90_000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSport, selectedCompetition]);

    // Auto-rotate hero carousel every 6 seconds
    useEffect(() => {
        if (heroSlides.length < 2) return;
        const t = setInterval(() => {
            setHeroIdx((i) => (i + 1) % heroSlides.length);
        }, 6000);
        return () => clearInterval(t);
    }, [heroSlides.length]);

    const fetchContent = async () => {
        try {
            let url = `${API}/content/public?limit=20&sport=${selectedSport}`;
            if (selectedCompetition) {
                url += `&competition_id=${selectedCompetition}`;
            }
            const response = await axios.get(url);
            const data = response.data;

            // Hero carousel: prioritize breaking, then featured, then recent — up to 5 slides
            const withImage = data.filter((a) => a.image_url);
            const priority = [
                ...withImage.filter((a) => a.is_breaking),
                ...withImage.filter((a) => a.is_featured && !a.is_breaking),
                ...withImage.filter((a) => !a.is_featured && !a.is_breaking),
            ];
            const slides = (priority.length ? priority : data).slice(0, 5);
            setHeroSlides(slides);
            setHeroIdx(0);

            const heroIds = new Set(slides.map((s) => s.id));
            // Set top headlines (excluding hero slides)
            const remaining = data.filter((a) => !heroIds.has(a.id));
            setTopHeadlines(remaining.slice(0, 6));
            setArticles(remaining.slice(6));
        } catch (error) {
            console.error("Error fetching content:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTanzaniaNews = async () => {
        try {
            const response = await axios.get(`${API}/content/public?limit=5&country=tanzania`);
            setTanzaniaNews(response.data);
        } catch (error) {
            console.error("Error fetching Tanzania news:", error);
        }
    };

    const fetchPredictions = async () => {
        try {
            // Use real ESPN upcoming fixtures with multi-AI consensus
            const response = await axios.get(`${API}/predictions/upcoming?limit=4`);
            setPredictions(response.data?.fixtures || []);
        } catch (error) {
            console.error("Error fetching predictions:", error);
        }
    };

    const fetchMatches = async () => {
        try {
            // Real ESPN live scores; fallback chain: today → recent → upcoming
            const sport = selectedSport === "football" ? "soccer" : selectedSport;
            const todayRes = await axios.get(`${API}/live-scores?window=today&sport=${sport}`);
            let items = todayRes.data?.matches || [];
            if (items.length === 0) {
                const recentRes = await axios.get(`${API}/live-scores?window=recent&sport=${sport}`);
                items = recentRes.data?.matches || [];
            }
            if (items.length === 0) {
                const upcomingRes = await axios.get(`${API}/live-scores?window=upcoming&sport=${sport}`);
                items = upcomingRes.data?.matches || [];
            }
            // Prioritise: live → finished today → upcoming today
            const stateOrder = { in: 0, post: 1, pre: 2 };
            items.sort((a, b) =>
                (stateOrder[a.state] ?? 3) - (stateOrder[b.state] ?? 3) ||
                (a.date || "").localeCompare(b.date || "")
            );
            setMatches(items.slice(0, 6));
        } catch (error) {
            console.error("Error fetching matches:", error);
        }
    };

    const formatDate = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
        
        if (diffHours < 1) return "Sasa hivi";
        if (diffHours < 24) return `Saa ${diffHours} zilizopita`;
        return date.toLocaleDateString("sw-TZ", { day: "numeric", month: "short" });
    };

    const formatMatchTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString("sw-TZ", { hour: "2-digit", minute: "2-digit" });
    };

    const competitions = COMPETITIONS[selectedSport] || {};

    return (
        <div className="min-h-screen bg-obsidian">
            {/* Top Navigation Bar — ESPN-style, bold brand */}
            <nav className="bg-electric border-b-4 border-black" data-testid="main-navigation">
                <div className="container-main">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
                            <div className="flex items-center justify-center w-10 h-10 bg-white rounded">
                                <Zap className="h-6 w-6 text-electric" strokeWidth={3} />
                            </div>
                            <span className="dimbani-wordmark text-3xl sm:text-4xl text-white">
                                DIMBANI
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-6">
                            {user ? (
                                <div className="flex items-center gap-3">
                                    {isContentManager && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => navigate("/admin")}
                                            className="text-white hover:bg-white/10 font-bold"
                                        >
                                            <LayoutDashboard className="h-4 w-4 mr-1" />
                                            Msimamizi
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={logout}
                                        className="text-white hover:bg-white/10"
                                    >
                                        <LogOut className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate("/login")}
                                    className="text-white hover:bg-white/10 font-bold"
                                >
                                    <LogIn className="h-4 w-4 mr-1" />
                                    Ingia
                                </Button>
                            )}
                        </div>

                        <button
                            className="md:hidden text-white"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Live Scores Ticker */}
            <LiveScoresTicker />

            {/* Secondary Navigation - Sports */}
            <div className="bg-paper border-b border-border">
                <div className="container-main">
                    <div className="flex items-center h-12 gap-1 overflow-x-auto scrollbar-hide">
                        {SPORTS.map(sport => (
                            <button
                                key={sport.id}
                                onClick={() => {
                                    setSelectedSport(sport.id);
                                    setSelectedCompetition(null);
                                }}
                                className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 border-b-2 ${
                                    selectedSport === sport.id 
                                        ? "border-electric text-electric" 
                                        : "border-transparent text-white/70 hover:text-white"
                                }`}
                            >
                                <span>{sport.icon}</span>
                                <span>{sport.name}</span>
                            </button>
                        ))}
                        <Separator orientation="vertical" className="h-6 mx-2 bg-border" />
                        {NAV_ITEMS.map(item => (
                            <Link
                                key={item.id}
                                to={item.link || "/"}
                                className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white whitespace-nowrap"
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Competition Filter */}
            {selectedSport === "football" && (
                <div className="bg-subtle border-b border-border">
                    <div className="container-main">
                        <ScrollArea className="w-full">
                            <div className="flex items-center h-10 gap-4">
                                <button
                                    onClick={() => setSelectedCompetition(null)}
                                    className={`text-xs font-medium whitespace-nowrap ${
                                        !selectedCompetition ? "text-electric" : "text-white/50 hover:text-white"
                                    }`}
                                >
                                    Zote
                                </button>
                                {Object.entries(competitions).map(([country, comps]) => (
                                    comps.map(comp => (
                                        <button
                                            key={comp.id}
                                            onClick={() => setSelectedCompetition(comp.id)}
                                            className={`text-xs font-medium whitespace-nowrap flex items-center gap-1 ${
                                                selectedCompetition === comp.id 
                                                    ? "text-electric" 
                                                    : "text-white/50 hover:text-white"
                                            }`}
                                        >
                                            <span>{COUNTRIES[country]?.flag}</span>
                                            <span>{comp.name_sw}</span>
                                        </button>
                                    ))
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                </div>
            )}

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-paper border-b border-border animate-slide-up">
                    <div className="container-main py-4 space-y-3">
                        {NAV_ITEMS.map(item => (
                            <Link
                                key={item.id}
                                to={item.link || "/"}
                                className="block text-white py-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {item.name}
                            </Link>
                        ))}
                        {user ? (
                            <>
                                {isContentManager && (
                                    <Button variant="outline" onClick={() => navigate("/admin")} className="w-full">
                                        Msimamizi
                                    </Button>
                                )}
                                <Button variant="outline" onClick={logout} className="w-full">Toka</Button>
                            </>
                        ) : (
                            <Button onClick={() => navigate("/login")} className="btn-primary w-full">Ingia</Button>
                        )}
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="container-main py-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Main Column */}
                    <div className="lg:col-span-8">
                        {/* Hero Carousel — ESPN style: image only, text below */}
                        {heroSlides.length > 0 ? (
                            <div className="mb-8" data-testid="hero-carousel">
                                {/* Image only — no overlay text */}
                                <Link
                                    to={`/makala/${heroSlides[heroIdx].id}`}
                                    className="block relative group"
                                    data-testid="hero-article"
                                >
                                    <div className="relative aspect-[16/10] sm:aspect-[16/9] rounded-sm overflow-hidden bg-paper">
                                        {heroSlides.map((slide, i) => (
                                            <img
                                                key={slide.id}
                                                src={upgradeImageUrl(slide.image_url, 1600) || FALLBACK_HERO}
                                                alt=""
                                                loading={i === 0 ? "eager" : "lazy"}
                                                onError={(e) => { e.currentTarget.src = FALLBACK_HERO; }}
                                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                                                    i === heroIdx ? "opacity-100" : "opacity-0"
                                                }`}
                                            />
                                        ))}
                                        {heroSlides[heroIdx].is_breaking && (
                                            <Badge className="absolute top-4 left-4 bg-electric text-white border-0 uppercase tracking-widest text-xs animate-pulse">
                                                Habari Mpya
                                            </Badge>
                                        )}

                                        {/* Carousel Indicators — minimal, bottom */}
                                        {heroSlides.length > 1 && (
                                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" data-testid="hero-indicators">
                                                {heroSlides.map((_, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setHeroIdx(i);
                                                        }}
                                                        data-testid={`hero-dot-${i}`}
                                                        aria-label={`Slide ${i + 1}`}
                                                        className={`h-1.5 rounded-full transition-all duration-300 ${
                                                            i === heroIdx
                                                                ? "w-6 bg-white"
                                                                : "w-1.5 bg-white/50 hover:bg-white/80"
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Title + summary + byline BELOW the image — ESPN style */}
                                    <div className="pt-4 pb-2 group-hover:opacity-90 transition-opacity">
                                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-white leading-tight tracking-tight group-hover:text-electric transition-colors">
                                            {heroSlides[heroIdx].title_sw}
                                        </h1>
                                        {(heroSlides[heroIdx].summary_sw || heroSlides[heroIdx].content_sw) && (
                                            <p className="text-white/70 text-base mt-2 line-clamp-2">
                                                {heroSlides[heroIdx].summary_sw ||
                                                    heroSlides[heroIdx].content_sw?.slice(0, 180)}
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2 mt-2 text-white/40 text-xs">
                                            <span>{formatDate(heroSlides[heroIdx].published_at || heroSlides[heroIdx].created_at)}</span>
                                            {heroSlides[heroIdx].author_name && (
                                                <>
                                                    <span>·</span>
                                                    <span>{heroSlides[heroIdx].author_name}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : (
                            <div className="aspect-[16/9] rounded-sm bg-paper flex items-center justify-center mb-6">
                                <div className="text-center">
                                    <Zap className="h-16 w-16 text-electric mx-auto mb-4" />
                                    <h2 className="text-3xl dimbani-wordmark text-white">DIMBANI</h2>
                                    <p className="text-white/50">Habari za Michezo</p>
                                </div>
                            </div>
                        )}

                        {/* Top Headlines Section */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between gap-2 mb-4 pb-2 border-b-2 border-electric">
                                <div className="flex items-center gap-2">
                                    <List className="h-5 w-5 text-electric" />
                                    <h2 className="font-heading font-bold text-lg text-white uppercase">
                                        Vichwa vya Habari
                                    </h2>
                                </div>
                                <button
                                    onClick={() => {
                                        fetchContent();
                                        fetchTanzaniaNews();
                                        fetchMatches();
                                    }}
                                    className="text-white/40 hover:text-electric text-xs font-medium uppercase tracking-wider transition-colors flex items-center gap-1"
                                    title="Sasisha habari mpya"
                                    data-testid="refresh-headlines-btn"
                                >
                                    <RefreshCcw className="h-3 w-3" />
                                    Sasisha
                                </button>
                            </div>
                            <div className="space-y-3">
                                {topHeadlines.map((article, idx) => (
                                    <Link
                                        key={article.id}
                                        to={`/makala/${article.id}`}
                                        className="flex items-start gap-3 group py-2 border-b border-border/30 last:border-0"
                                        data-testid={`headline-${idx}`}
                                    >
                                        <span className="text-electric font-mono text-sm mt-1">•</span>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-white text-sm font-medium group-hover:text-electric transition-colors line-clamp-2">
                                                {article.title_sw}
                                            </h3>
                                            {article.team && (
                                                <span className="text-white/40 text-xs">{article.team}</span>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Tanzania News Section */}
                        {tanzaniaNews.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-solar">
                                    <span className="text-xl">🇹🇿</span>
                                    <h2 className="font-heading font-bold text-lg text-white uppercase">
                                        Habari za Tanzania
                                    </h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {tanzaniaNews.map((article, idx) => (
                                        <Link
                                            key={article.id}
                                            to={`/makala/${article.id}`}
                                            className="group flex gap-3"
                                            data-testid={`tanzania-news-${idx}`}
                                        >
                                            <img
                                                src={upgradeImageUrl(article.image_url, 800) || FALLBACK_THUMB}
                                                alt={article.title_sw}
                                                className="w-24 h-16 object-cover rounded shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-white text-sm font-medium group-hover:text-solar transition-colors line-clamp-2">
                                                    {article.title_sw}
                                                </h3>
                                                <p className="text-white/40 text-xs mt-1">
                                                    {formatDate(article.published_at || article.created_at)}
                                                </p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* World News Section removed — all external RSS items are translated and surface via published Content */}

                        {/* More Articles */}
                        {articles.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border">
                                    <Newspaper className="h-5 w-5 text-white/50" />
                                    <h2 className="font-heading font-bold text-lg text-white uppercase">
                                        Habari Zaidi
                                    </h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {articles.map((article, idx) => (
                                        <Link
                                            key={article.id}
                                            to={`/makala/${article.id}`}
                                            className="group flex gap-3 py-3 border-b border-border/30 last:border-0"
                                            data-testid={`article-${idx}`}
                                        >
                                            <img
                                                src={upgradeImageUrl(article.image_url, 800) || FALLBACK_THUMB}
                                                alt={article.title_sw}
                                                className="w-28 h-20 object-cover rounded shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <Badge variant="outline" className="mb-1 text-xs border-white/20 text-white/50">
                                                    {CONTENT_TYPE_SW[article.content_type] || "Habari"}
                                                </Badge>
                                                <h3 className="text-white text-sm font-medium group-hover:text-electric transition-colors line-clamp-2">
                                                    {article.title_sw}
                                                </h3>
                                                <p className="text-white/40 text-xs mt-1 line-clamp-1">
                                                    {article.summary_sw || article.content_sw?.slice(0, 80)}
                                                </p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-4 space-y-6">
                        {/* Scores Widget */}
                        <Card className="bg-paper border-border overflow-hidden">
                            <div className="bg-electric px-4 py-2">
                                <h3 className="font-heading font-bold text-sm text-white uppercase flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Matokeo ya Mechi
                                </h3>
                            </div>
                            <div className="p-3 space-y-2">
                                {matches.length > 0 ? matches.slice(0, 5).map((match, idx) => {
                                    const isLive = match.state === "in";
                                    const isFinished = match.state === "post";
                                    return (
                                        <Link
                                            key={`${match.id}-${idx}`}
                                            to="/matokeo"
                                            className="block p-2 hover:bg-subtle rounded transition-colors"
                                            data-testid={`match-score-${idx}`}
                                        >
                                            <div className="flex items-center justify-between text-xs text-white/40 mb-1">
                                                <span className="truncate flex items-center gap-1">
                                                    <span>{match.flag}</span>
                                                    <span>{match.competition_name_sw || match.competition_name}</span>
                                                </span>
                                                {isFinished && <Badge className="bg-white/10 text-white/50 text-[10px]">FT</Badge>}
                                                {isLive && <Badge className="bg-red-500 text-white text-[10px] animate-pulse">{match.minute_label || "LIVE"}</Badge>}
                                                {!isLive && !isFinished && <span className="text-[10px]">{match.minute_label || formatMatchTime(match.date)}</span>}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-white text-sm flex-1 truncate">{match.home_name}</span>
                                                <span className={`font-mono font-bold w-6 text-center ${isLive || isFinished ? "text-electric" : "text-white/30"}`}>
                                                    {(isLive || isFinished) ? (match.home_score ?? "-") : "-"}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-white text-sm flex-1 truncate">{match.away_name}</span>
                                                <span className={`font-mono font-bold w-6 text-center ${isLive || isFinished ? "text-electric" : "text-white/30"}`}>
                                                    {(isLive || isFinished) ? (match.away_score ?? "-") : "-"}
                                                </span>
                                            </div>
                                        </Link>
                                    );
                                }) : (
                                    <div className="text-center py-6 text-white/40 text-sm">
                                        Hakuna mechi kwa sasa
                                    </div>
                                )}
                                <Link
                                    to="/matokeo"
                                    className="block text-center text-electric text-xs font-medium py-2 hover:underline"
                                >
                                    Tazama Mechi Zote →
                                </Link>
                            </div>
                        </Card>

                        {/* Daily Top-3 Auto Picks */}
                        <DailyPicksCard />

                        {/* Predictions Widget */}
                        <Card className="bg-paper border-border overflow-hidden">
                            <div className="bg-solar px-4 py-2">
                                <h3 className="font-heading font-bold text-sm text-black uppercase flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    Utabiri
                                </h3>
                            </div>
                            <div className="p-3 space-y-3">
                                {predictions.length > 0 ? predictions.map((pred, idx) => (
                                    <Link
                                        key={pred.id}
                                        to="/utabiri"
                                        className="block p-2 hover:bg-subtle rounded transition-colors"
                                        data-testid={`prediction-${idx}`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-white/40 text-xs flex items-center gap-1 truncate">
                                                <span>{pred.flag}</span>
                                                <span>{pred.competition_name_sw || pred.competition_name}</span>
                                            </span>
                                            <Badge className="bg-electric/10 text-electric text-xs">
                                                {pred.confidence}%
                                            </Badge>
                                        </div>
                                        <p className="text-white text-sm font-medium truncate">
                                            {pred.home_team} vs {pred.away_team}
                                        </p>
                                        <p className="text-white/50 text-xs line-clamp-1 mt-1">
                                            <span className="text-solar font-bold">{pred.winner}</span>
                                            {pred.top_reason_sw ? ` — ${pred.top_reason_sw}` : ""}
                                        </p>
                                    </Link>
                                )) : (
                                    <div className="text-center py-6 text-white/40 text-sm">
                                        Utabiri mpya unakuja
                                    </div>
                                )}
                                <Link
                                    to="/utabiri"
                                    className="block text-center text-solar text-xs font-medium py-2 hover:underline"
                                >
                                    Tazama Utabiri Wote →
                                </Link>
                            </div>
                        </Card>

                        {/* SMS CTA */}
                        <Card className="bg-subtle border-border p-4">
                            <p className="text-white/60 text-xs mb-2">Pata habari kwa SMS</p>
                            <p className="text-electric font-mono text-2xl font-bold text-center">22244</p>
                            <p className="text-white/40 text-xs text-center mt-2">Tuma swali lako</p>
                        </Card>

                        {/* Web Subscribe Widget */}
                        <WebSubscribeWidget />
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-border bg-paper">
                <div className="container-main py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Zap className="h-5 w-5 text-electric" />
                            <span className="dimbani-wordmark text-lg text-white">DIMBANI</span>
                        </div>
                        <p className="text-white/40 text-xs">
                            &copy; {new Date().getFullYear()} Dimbani Sports. Haki zote zimehifadhiwa.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePage;
